import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { token } from '@atlaskit/tokens';
import { type GlobalShortcutsProps, useOrderedAppShortcuts } from '@atlassian/global-shortcuts';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_APPS,
	type InvitedAppShortcutAllowedAppKey,
	type ShortcutItemsConfig,
} from '../../common/types';

import { InvitedAppPopup } from './invited-app-shortcuts-container/invited-app-popup';
import { ShortcutsMenu, type ShortcutsMenuProps } from './shortcuts-menu';

interface InvitedAppShortcutsProps
	extends Pick<GlobalShortcutsProps, 'product'>,
		Pick<ShortcutsMenuProps, 'invitedAppProductLinks' | 'openInSameTab' | 'forceStatic'> {
	hoverDelayTimeMs?: number;
	shortcutItemsConfig: ShortcutItemsConfig;
}

/**
 * This component is deprecated. We will soon remove it.
 * @deprecated Use InvitedAppShortcutsContainer and ShortcutsMenu instead
 */
export const InvitedAppShortcuts = ({
	shortcutItemsConfig,
	product,
	hoverDelayTimeMs = 300,
	forceStatic = false,
	openInSameTab = false,
	invitedAppProductLinks,
}: InvitedAppShortcutsProps) => {
	// the popup shown state
	const [isPopupShown, setIsPopupShown] = useState(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);

	// refs for the shortcut items
	const jiraShortcutRef = useRef<HTMLAnchorElement | null>(null);
	const confluenceShortcutRef = useRef<HTMLAnchorElement | null>(null);

	// Store listener functions so we can properly remove them
	const listenerFunctionsRef = useRef<
		Map<
			React.MutableRefObject<HTMLAnchorElement | null>,
			{
				blur: VoidFunction;
				focus: VoidFunction;
				mouseleave: VoidFunction;
				mouseover: VoidFunction;
			}
		>
	>(new Map());

	// Ref for the current shortcut item
	const [currentShortcutRef, setCurrentShortcutRef] =
		useState<React.MutableRefObject<HTMLAnchorElement | null>>();

	// map of the shortcut items to their refs
	const shortcutRefs: Record<
		InvitedAppShortcutAllowedAppKey,
		React.MutableRefObject<HTMLAnchorElement | null>
	> = useMemo(
		() => ({
			jira: jiraShortcutRef,
			confluence: confluenceShortcutRef,
		}),
		[jiraShortcutRef, confluenceShortcutRef],
	);

	const showPopup = useCallback(() => {
		setIsPopupShown(true);
	}, [setIsPopupShown]);

	const hidePopup = useCallback(() => {
		setIsPopupShown(false);
		setCurrentShortcutRef(undefined);
	}, [setIsPopupShown, setCurrentShortcutRef]);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const startClosePopupTimer = useCallback(() => {
		clearClosePopupTimer();
		closePopupTimerRef.current = setTimeout(() => {
			hidePopup();
		}, hoverDelayTimeMs);
	}, [clearClosePopupTimer, hoverDelayTimeMs, hidePopup]);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse over event on the shortcut item
	 */
	const handleMouseOverWithRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) => () => {
			// clear the close popup timer to avoid closing the popup
			// when moving the mouse from the popup to the shortcut item
			clearClosePopupTimer();

			setCurrentShortcutRef(targetRef);
			showPopup();
		},
		[setCurrentShortcutRef, showPopup, clearClosePopupTimer],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse leave event on the shortcut item
	 */
	const handleMouseLeaveWithRef = useCallback(
		() => () => {
			if (currentShortcutRef?.current) {
				currentShortcutRef.current.style.backgroundColor = '';
			}

			startClosePopupTimer();
		},
		[currentShortcutRef, startClosePopupTimer],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a callback ref for a shortcut item
	 */
	const createShortcutRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) =>
			(node: HTMLAnchorElement | null) => {
				// Cleanup previous element if it exists
				if (targetRef.current) {
					const listeners = listenerFunctionsRef.current.get(targetRef);
					if (listeners) {
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						targetRef.current.removeEventListener('mouseover', listeners.mouseover);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						targetRef.current.removeEventListener('focus', listeners.focus);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						targetRef.current.removeEventListener('mouseleave', listeners.mouseleave);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						targetRef.current.removeEventListener('blur', listeners.blur);
						listenerFunctionsRef.current.delete(targetRef);
					}
				}

				// Store the new element
				targetRef.current = node;

				// Attach listeners to new element
				if (node) {
					// Create and store listener functions so we can remove them later
					const mouseoverListener = handleMouseOverWithRef(targetRef);
					const focusListener = handleMouseOverWithRef(targetRef);
					const mouseleaveListener = handleMouseLeaveWithRef();
					const blurListener = handleMouseLeaveWithRef();

					listenerFunctionsRef.current.set(targetRef, {
						mouseover: mouseoverListener,
						focus: focusListener,
						mouseleave: mouseleaveListener,
						blur: blurListener,
					});

					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('mouseover', mouseoverListener);
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('focus', focusListener);
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('mouseleave', mouseleaveListener);
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('blur', blurListener);
				}
			},
		[handleMouseOverWithRef, handleMouseLeaveWithRef],
	);

	/**
	 * Handles the popup shown event
	 * @returns a handler for the popup shown event
	 */
	const handleOnMouseEnterPopup = useCallback(() => {
		// clear the close popup timer to avoid closing the popup too early
		clearClosePopupTimer();

		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const parentElement = currentShortcutRef.current.parentElement;

			if (parentElement) {
				// reference the background color variable from the design system
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/4ff76dcfb3ce12b4344e5345be748ce472ac56e7/platform/packages/design-system/navigation-system/src/ui/menu-item/menu-item.tsx#lines-166
				parentElement.style.backgroundColor = token('elevation.surface.hovered');
			}

			// find the last child element (icon) of the parent element
			const lastChildElement = parentElement?.lastElementChild;
			if ((lastChildElement as HTMLElement)?.style) {
				// set visibility to hidden to the last child element
				(lastChildElement as HTMLElement).style.visibility = 'hidden';
			}
		}

		// ensure the popup is shown when mouse leaves the shortcut item and over the popup
		showPopup();
	}, [clearClosePopupTimer, showPopup, currentShortcutRef]);

	/**
	 * Handles the popup closed event
	 * @returns a handler for the popup closed event
	 */
	const handleOnMouseLeavePopup = useCallback(() => {
		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const parentElement = currentShortcutRef.current.parentElement;

			if (parentElement) {
				parentElement.style.backgroundColor = '';
			}

			const lastChildElement = parentElement?.lastElementChild;
			if ((lastChildElement as HTMLElement)?.style) {
				// set visibility to visible to the last child element
				(lastChildElement as HTMLElement).style.visibility = 'visible';
			}
		}
		startClosePopupTimer();
	}, [currentShortcutRef, startClosePopupTimer]);

	const shortcutItemsWithRef: ShortcutItemsConfig = useMemo(
		() =>
			Object.entries(shortcutItemsConfig).reduce((acc, [key, value]) => {
				if (INVITED_APP_SHORTCUTS_ALLOWED_APPS.includes(key as InvitedAppShortcutAllowedAppKey)) {
					acc[key as InvitedAppShortcutAllowedAppKey] = {
						...value,
						// @ts-expect-error - callback ref is valid but not typed
						ref: createShortcutRef(shortcutRefs[key as InvitedAppShortcutAllowedAppKey]),
					};
				}
				return acc as ShortcutItemsConfig;
			}, {} as ShortcutItemsConfig),
		[shortcutItemsConfig, createShortcutRef, shortcutRefs],
	);

	// Sort shortcut items based on the ordered menu config
	const orderedShortcuts = useOrderedAppShortcuts(shortcutItemsWithRef);

	useEffect(() => {
		const currentListeners = listenerFunctionsRef.current;
		const currentShortcutRefs = shortcutRefs;

		return () => {
			// clean up the current ref's listeners
			Object.values(currentShortcutRefs).forEach((ref) => {
				if (ref.current) {
					const listeners = currentListeners.get(ref);
					if (listeners) {
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						ref.current.removeEventListener('mouseover', listeners.mouseover);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						ref.current.removeEventListener('focus', listeners.focus);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						ref.current.removeEventListener('mouseleave', listeners.mouseleave);
						// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
						ref.current.removeEventListener('blur', listeners.blur);
						currentListeners.delete(ref);
					}
				}
			});

			clearClosePopupTimer();
		};
	}, [clearClosePopupTimer, shortcutRefs]);

	return (
		<>
			<ShortcutsMenu
				shortcutItems={orderedShortcuts}
				forceStatic={forceStatic}
				product={product}
				openInSameTab={openInSameTab}
				invitedAppProductLinks={invitedAppProductLinks}
			/>
			{isPopupShown && currentShortcutRef && (
				<InvitedAppPopup
					trigger={currentShortcutRef}
					onMouseEnterPopup={handleOnMouseEnterPopup}
					onMouseLeavePopup={handleOnMouseLeavePopup}
				>
					{/* TODO: render the content based on the product and collaborators */}
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<div>InvitedAppShortcuts</div>
				</InvitedAppPopup>
			)}
		</>
	);
};
